import React from "react";
import { Link } from "react-router-dom";
import { badgeStyle, cardStyle, statusLabel } from "../../components/ui";

// Small pieces shared by the Procurement screens (buyer and supplier). Tables are the app's standard
// DataTable (the one every Finance / Dealership screen uses), re-exported here.
export { DataTable, act, linkBtn, hint, downloadCsv } from "../Fin/gl/glUi";

const COLOURS = {
  ACTIVE: ["#D1FAE5", "#065F46"],
  INVITED: ["#DBEAFE", "#1E40AF"],
  SUSPENDED: ["#FEE2E2", "#991B1B"],
  INACTIVE: ["#F3F4F6", "#4B5563"],
  PENDING: ["#FEF3C7", "#92400E"],
  APPROVED: ["#D1FAE5", "#065F46"],
  REJECTED: ["#FEE2E2", "#991B1B"],
  COMPLETE: ["#D1FAE5", "#065F46"],
  EXPIRED: ["#FEE2E2", "#991B1B"],
  // RFQs and quotations
  DRAFT: ["#F3F4F6", "#4B5563"],
  LIVE: ["#DBEAFE", "#1E40AF"],
  EVALUATION: ["#FEF3C7", "#92400E"],
  CLOSED: ["#F3F4F6", "#4B5563"],
  CANCELLED: ["#FEE2E2", "#991B1B"],
  OPEN: ["#D1FAE5", "#065F46"],
  INFO: ["#FEF3C7", "#92400E"],
  QUOTED: ["#DBEAFE", "#1E40AF"],
  QUALIFIED: ["#D1FAE5", "#065F46"],
  DECLINED: ["#F3F4F6", "#4B5563"],
  AWARDED: ["#D1FAE5", "#065F46"],
  NOT_AWARDED: ["#F3F4F6", "#4B5563"],
  ACCEPTED: ["#D1FAE5", "#065F46"],
  SHIPPED: ["#DBEAFE", "#1E40AF"],
  DELIVERED: ["#D1FAE5", "#065F46"],
  COMPLETED: ["#E5E7EB", "#111827"],
  PENDING_APPROVAL: ["#FEF3C7", "#92400E"],
  WITHDRAWN: ["#F3F4F6", "#4B5563"],
};
const LABELS = { INVITED: "Invited", COMPLETE: "Documents complete", PENDING: "Pending", EXPIRED: "Expired", EVALUATION: "Under evaluation", INFO: "Information requested", QUOTED: "Quotation sent", NOT_AWARDED: "Not awarded", PENDING_APPROVAL: "Waiting for approval", DRAFT: "Draft" };

// The badge key of an RFQ row (status numbers are ezp_proc_order_status).
export const rfqKey = (r) => (r.status === 0 ? "DRAFT" : r.status === 1 ? "LIVE" : r.status === 13 ? "EVALUATION" : r.status === 3 ? "AWARDED" : r.closed_reason === "CANCELLED" ? "CANCELLED" : "CLOSED");
// The badge key of a quotation status: 2 sent, 13 under evaluation, 11 qualified, 4 rejected.
export const quoteKey = (status) => (status === 3 ? "AWARDED" : status === 14 ? "NOT_AWARDED" : status === 11 ? "QUALIFIED" : status === 4 ? "REJECTED" : status === 13 ? "EVALUATION" : "QUOTED");

export function ProcBadge({ status, label }) {
  const [bg, color] = COLOURS[status] || ["#F3F4F6", "#374151"];
  return <span style={badgeStyle(bg, color)}>{label || LABELS[status] || statusLabel(status)}</span>;
}

export const today = () => new Date().toISOString().slice(0, 10);
export const amount = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// A document's size for people.
export const fileSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// Opens a signed file link in a new tab.
export async function openSigned(getUrl, toast) {
  try {
    const res = await getUrl();
    if (res?.data?.url) window.open(res.data.url, "_blank", "noopener");
    else toast.error(res?.message || "The file could not be opened");
  } catch (err) {
    toast.error(err.message || "The file could not be opened");
  }
}

// The badge key of an order (ezp_proc_order_status): 3 awarded, 6 accepted, 4 rejected ...
export const orderKey = (status) => (status === 15 ? "COMPLETED" : status === 10 ? "DELIVERED" : status === 8 || status === 9 ? "SHIPPED" : status === 6 || status === 7 ? "ACCEPTED" : status === 4 ? "REJECTED" : "AWARDED");

// A number with a label, linking to the list behind it. Tiles wrap to as many columns as fit (one on a small phone).
export function StatTile({ label, value, to, tone = "#111827", note }) {
  const body = (
    <>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tone, lineHeight: 1.2 }}>{value}</div>
      {note ? <div style={{ fontSize: 12, color: "#6B7280" }}>{note}</div> : null}
    </>
  );
  const style = { ...cardStyle, padding: "14px 16px", textDecoration: "none", display: "block", textAlign: "left" };
  return to ? <Link to={to} style={style}>{body}</Link> : <div style={style}>{body}</div>;
}

export const TileGrid = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }} className="mb-4">{children}</div>;

// The date for a CSV file name.
export const csvStamp = () => new Date().toISOString().slice(0, 10);
